// JsonKeyValueTable.jsx
import React, { useMemo } from "react";
import { Paper, Table, TableHead, TableRow, TableCell, TableBody, Typography, Box, Chip } from "@mui/material";

// Aplana objetos anidados: {a:{b:1}} -> [["a.b", 1]]
function flatten(obj, prefix = "", out = []) {
  if (obj === null || obj === undefined) return out;
  if (typeof obj !== "object" || Array.isArray(obj)) {
    out.push([prefix || "(valor)", obj]);
    return out;
  }
  for (const [k, v] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      flatten(v, path, out);
    } else {
      out.push([path, v]);
    }
  }
  return out;
}

function renderValue(key, value) {
  if (typeof value === "boolean") {
    return <Chip size="small" label={value ? "true" : "false"} color={value ? "success" : "default"} />;
  }
  if (typeof value === "number") {
    return <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{value}</Typography>;
  }
  if (typeof value === "string") {
    return <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{value}</Typography>;
  }
  return (
    <Typography component="pre" variant="body2" sx={{ whiteSpace: "pre-wrap", m: 0, fontFamily: "monospace" }}>
      {JSON.stringify(value, null, 2)}
    </Typography>
  );
}

export default function JsonKeyValueTable({ data }) {
  const parsed = useMemo(() => {
    try {
      return typeof data === "string" ? JSON.parse(data) : (data ?? {});
    } catch {
      return { __error: "JSON inválido" };
    }
  }, [data]);

  const rows = useMemo(() => {
    const flat = flatten(parsed);
    flat.sort((a, b) => a[0].localeCompare(b[0]));
    return flat;
  }, [parsed]);

  return (
    <Paper variant="outlined" sx={{ p: 1 }}>
      <Box sx={{ borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "45%" }}><strong>Campo</strong></TableCell>
              <TableCell><strong>Valor</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(([k, v]) => (
              <TableRow key={k}>
                <TableCell sx={{ fontFamily: "monospace" }}>{k}</TableCell>
                <TableCell>{renderValue(k, v)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}
